import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import { GridLegacy as Grid, Stack } from '@mui/material'

import { memo } from 'react'

import LocationSelectionGraphql from '../LocationSelectionGraphql'
import SubsidyAndBalance from '../SubsidyAndBalance'
import { TimeslotSelectionGraphql } from '../TimeslotSelectionGraphql'
import { OrderTimeLeft } from './OrderTimeLeft'
import { ServiceSelectionGraphql } from './ServiceSelectionGraphql/index'

const canteenMenuNavBarCurrentMenuDataFragment = graphql(`
  fragment CanteenMenuNavBar_CurrentMenuDataFragment on CCMealService {
    availableSubsidy
    timeslots {
      id
      startTime
      endTime
    }
  }
`)

const canteenMenuNavBarUserDataFragment = graphql(`
  fragment CanteenMenuNavBar_UserDataFragment on User {
    accountLocation {
      timezone
    }
    wallet {
      balance
    }
    subsidySettings {
      unlimitedSubsidy
    }
    account {
      userInterfaceSettings {
        hideCCPrices
      }
      locationsPaged(filters: { isCCActive: true }) {
        rows {
          id
        }
      }
      topUpEnabled
    }
    ...CanteenServiceSelection_UserDataFragment
    ...CanteenLocationSelection_UserDataFragment
  }
`)

interface NavBarProps {
  currency: string
  date: string
  meal: string
  arrivalTime?: string | null
  menuClosed: boolean
  openingTime?: string
  orderTimeRemaining?: number
  menuData?: FragmentType<typeof canteenMenuNavBarCurrentMenuDataFragment> | null
  userData: FragmentType<typeof canteenMenuNavBarUserDataFragment>
  onChangeTimeslot: (timeslot: string) => void
  onChangeSelectedService: (input: { meal: string; date: string }) => void
  onChangeAccountLocation: (accountLocationId: number) => void
}

export const MenuNavBar = memo(function NavBar({
  date,
  meal,
  arrivalTime,
  menuClosed,
  openingTime,
  currency,
  orderTimeRemaining,
  menuData: menuDataProp,
  userData: userDataProp,
  onChangeAccountLocation,
  onChangeSelectedService,
  onChangeTimeslot,
}: NavBarProps) {
  const menuData = useFragment(canteenMenuNavBarCurrentMenuDataFragment, menuDataProp)
  const userData = useFragment(canteenMenuNavBarUserDataFragment, userDataProp)
  const hidePrices = Boolean(userData.account?.userInterfaceSettings.hideCCPrices)

  const locations = userData.account?.locationsPaged.rows || []
  const {
    wallet: { balance = 0 },
    subsidySettings: { unlimitedSubsidy = false },
  } = userData || {}
  const { availableSubsidy: subsidy = 0, timeslots = [] } = menuData || {}

  return (
    <Grid
      wrap="wrap"
      padding={1}
      alignContent="center"
      alignItems="center"
      container
      spacing={1}
      width="100%">
      {locations.length > 1 && (
        <Grid item xs={12} sm={12} md={6} lg={3} xl={3}>
          <LocationSelectionGraphql onChange={onChangeAccountLocation} userData={userData} />
        </Grid>
      )}
      <Grid item xs={12} sm={hidePrices ? 12 : 6} md={6} lg={3} xl={3}>
        <ServiceSelectionGraphql
          onChangeSelectedService={onChangeSelectedService}
          selectedDate={date}
          selectedMeal={meal}
          serviceData={userData}
        />
      </Grid>
      {arrivalTime && !menuClosed ? (
        <Grid item xs={12} sm={6} md={6} lg={2} xl={2}>
          <TimeslotSelectionGraphql
            timezone={userData.accountLocation?.timezone}
            onChangeTimeslot={onChangeTimeslot}
            selectedTimeslot={arrivalTime}
            timeslots={timeslots}
          />
        </Grid>
      ) : null}
      {arrivalTime && (
        <Grid item xs={hidePrices ? 12 : 6} sm={6} md={hidePrices ? 6 : 3} lg={2} xl={1}>
          <OrderTimeLeft
            timezone={userData.accountLocation?.timezone}
            menuClosed={menuClosed}
            openingTime={menuData ? openingTime : null}
            orderTimeRemaining={menuData ? orderTimeRemaining : null}
          />
        </Grid>
      )}
      {menuData && !hidePrices ? (
        <Grid
          display="flex"
          flexDirection="row-reverse"
          marginLeft="auto"
          minWidth="200px"
          width="100%"
          item
          xs={6}
          sm={6}
          md={3}
          lg={2}
          xl={2}>
          <Stack width="100%" maxWidth={{ xl: '200px' }}>
            <SubsidyAndBalance
              currency={currency}
              subsidy={subsidy}
              unlimitedSubsidy={unlimitedSubsidy}
              balance={balance}
              topUpEnabled={userData.account?.topUpEnabled}
            />
          </Stack>
        </Grid>
      ) : null}
    </Grid>
  )
})
